<template>
  <div class="container py-5">
    <div class="buttons pb-3">
      <button
        @click="adding = true"
        class="button is-primary">
        Add a Workstation
      </button>
      <button class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete()">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} Selected Workstations</span>
      </button>
    </div>

    <article 
      v-if="!loading && workstations.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no workstations.
      </div>
    </article>

    <table 
      v-if="!loading && workstations.length > 0"
      class="table is-fullwidth is-hoverable">

      <thead>
        <tr>
          <th>
            <v-checkbox 
              :value="allSelected"
              @input="toggleAllSelected($event)"/>
          </th>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr 
          class="v-centered"
          v-for="workstation in workstations"
          :key="workstation.id">

          <td>
            <v-checkbox 
              :value="selected.includes(workstation.id)"
              @input="toggleSelected(workstation.id)"/>
          </td>

          <!-- Not in edit mode -->
          <template v-if="workstation.id != editingId">
            <td>
              {{ workstation.workstation_name }}
            </td>
            <td>
              {{ workstation.desc }}
            </td>
            <td>
              {{ workstation.status }}
            </td>
            <td>
              <v-checkbox
                disabled
                v-model="workstation.is_active"/>
            </td>
            <td>
              {{ workstation.capacity_minutes | round(0) }}
            </td>
            <td>
              {{ workstation.capacity_minutes_with_ot | round(0) }}
            </td>
            <td>
              {{ workstation.queued_minutes }}
            </td>
            <td>
              <ul v-if="workstation.workstation_steps.length > 0">
                <li 
                  v-for="step in workstation.workstation_steps"
                  :key="step.id">
                  {{ step.step.work_name }}
                </li>
              </ul>
              <span v-else class="has-text-grey-light">
                No task types for this workstation
              </span>
            </td>
            <td>
              {{ workstation.pending_work_steps }}
            </td>
          </template>

          <!-- Edit mode -->
          <template v-else>
            <td>
              <v-text-field
                required
                placeholder="Workstation name"
                v-model="workstation.workstation_name"/>
            </td>
            <td>
              <v-text-field
                placeholder="Workstation description"
                v-model="workstation.desc"/>
            </td>
            <td>
              <v-select
                required
                :options="statuses"
                :value="workstation.status"
                @input="changeStatus(workstation, $event)"
              />
            </td>
            <td>
              <v-checkbox
                :value="workstation.is_active"
                @input="changeActive(workstation, $event)"
              />
            </td>
            <td>
              <v-text-field 
                type="number"
                placeholder="Daily capacity in minutes"
                v-model="workstation.capacity_minutes"
              />
            </td>
            <td>
              <v-text-field 
                type="number"
                placeholder="Daily capacity in minutes with OT"
                v-model="workstation.capacity_minutes_with_ot"
              />
            </td>
            <td>
              {{ workstation.queued_minutes }}
            </td>
            <td>
              <a @click="editWorkSteps(workstation)">
                Edit workstation work steps
              </a>
            </td>
            <td>
               {{ workstation.pending_work_steps }}
            </td>
          </template>

          <td>
            <div class="buttons buttons-right">
              <template v-if="workstation.id != editingId">
                <button 
                  @click="editingId = workstation.id"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                  <span>Edit</span>
                </button>
                <button 
                  @click="launchConfirmDelete(workstation.id)"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>Delete</span>
                </button>
              </template>

              <template v-else>
                <button 
                  @click="saveEdits(workstation)"
                  class="button is-small is-primary">
                  <span class="icon">
                    <i class="fas fa-save"></i>
                  </span>
                  <span>Save Edits</span>
                </button>
                <button 
                  @click="editingId = null"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-times"></i>
                  </span>
                  <span>Exit Editing</span>
                </button>
              </template>
            </div>
          </td>

        </tr>
      </tbody>

    </table>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <add-workstation-modal v-model="adding"/>

    <edit-workstation-work-steps-modal 
      v-if="editingWorkSteps.workstation"
      v-model="editingWorkSteps.state"
      :workstation="editingWorkSteps.workstation"/>
    
    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import AddWorkstationModal from '@/components/scheduling/AddWorkstationModal'
import EditWorkstationWorkStepsModal from '@/components/scheduling/EditWorkstationWorkStepsModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VCheckbox,
    VTextField,
    VSelect,
    VConfirmModal, 
    VPagination,
    AddWorkstationModal,
    EditWorkstationWorkStepsModal
  },
  data() {
    return {
      headers: [
        'Name', 'Description', 'Status', 'Is Active', 'Daily Capacity (minutes)', 
        'Daily Capacity (minutes) w/ OT',
        'Queued Minutes', 'Workstation Task Types', 'Pending Work Steps', ''
      ],
      editingId: null,
      adding: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this workstation?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      editingWorkSteps: {
        state: false,
        workstation: null
      }
    }
  },
  computed: {
    ...mapState("workstations", [
      "workstations", "loading", "selected", "allSelected",
      "filters", "pagination", "statuses"
    ])
  },
  watch: {
    'pagination.page'() {
      let params = {...this.pagination, ...this.filters};
      this.getWorkstations(params);
    },
    filters: {
      handler(filters) {
        this.getWorkstations(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("workstations", [
      "getWorkstations", "deleteWorkstation", "updateWorkstation", "toggleAllSelected"
    ]),
    ...mapMutations("workstations", [
      "toggleSelected", "changePage", "setFilter"
    ]),
    initialize() {
      if (this.workstations.length == 0) {
        this.getWorkstations();
      }
    },
    changeStatus(workstation, status) {
      workstation.status = status;
      if (status == 'Active') {
        workstation.is_active = true;
      } else {
        workstation.is_active = false;
      }
    },
    changeActive(workstation, active) {
      workstation.is_active = active;
      if (active) {
        workstation.status = 'Active';
      } else {
        workstation.status = 'Inactive';
      }
    },
    saveEdits(workstation) {
      this.updateWorkstation({
        workstationId: workstation.id,
        data: {
          'workstation_name': workstation.workstation_name,
          'desc': workstation.desc,
          'status': workstation.status,
          'is_active': workstation.is_active,
          'capacity_minutes': workstation.capacity_minutes
        }
      }).then(() => this.editingId = null);
    },
    editWorkSteps(workstation) {
      this.editingWorkSteps.workstation = workstation;
      this.editingWorkSteps.state = true;
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteWorkstation(id).then(
              () => this.confirmDelete.state = false
            )
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} workstations?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteWorkstation(id).then(
            () => this.confirmDelete.state = false
          )
        }
        this.confirmDelete.message = 'Are you sure you want to delete this workstation?'
      }
      this.confirmDelete.state = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>